import { AppSidebar } from '@/components/AppSidebar';
import { GenericFilterProvider } from '@/components/GenericFilter/context';
import { Header } from '@/components/Header';
import { useConfigStore } from '@/store/configStore';
import { useHeaderStore } from '@/store/headerStore';
import { cn } from '@/utils/utils';
import { SidebarProvider } from '@shelflix/ui-web/components/ui/sidebar';
import { Toaster } from 'sonner';
import { trpc } from '@/trpcClient';
import { useQuery } from '@tanstack/react-query';
import { useUserStore } from '@/store/userStore';
import { useEffect } from 'react';
import { LedPanelTranscoder } from '@/components/LedPanelTranscoder';
import { useHasScopes } from '@/hooks/useHasScopes';

export default function BaseLayout({ children, className }: { children: React.ReactNode; className?: string }) {
  const config = useConfigStore((state) => state.config);
  const { gradient } = useHeaderStore();
  const user = useUserStore((s) => s.user);
  const canEditMaterial = useHasScopes(['material:edit']);

  // For authenticated users, fetch the active tenant config to get tenant-specific branding
  // Use long staleTime to prevent unnecessary refetches that cause logo flickering
  // Presigned URLs are valid for 1 hour, so 30 minutes staleTime is safe
  const { data: tenantConfig } = useQuery({
    ...trpc.tenant.getActiveConfig.queryOptions(),
    enabled: !!user, // Only fetch when user is authenticated
    staleTime: 30 * 60 * 1000, // 30 minutes - prevents refetch on window focus
    gcTime: 60 * 60 * 1000, // 1 hour - keeps data in cache
  });

  // Use tenant config for logo if available, otherwise fall back to application config
  const activeConfig = tenantConfig ?? config;
  const logoUrl = gradient && activeConfig?.logoDarkUrl ? activeConfig?.logoDarkUrl : activeConfig?.logoUrl;

  // Update document title with tenant name
  useEffect(() => {
    if (tenantConfig?.name) {
      document.title = tenantConfig.name;
    }
  }, [tenantConfig?.name]);

  return (
    <SidebarProvider className="font-poppins">
      <GenericFilterProvider className="relative">
        <div className="flex w-full flex-col">
          <AppSidebar logo={logoUrl} />
          <Header logo={logoUrl} />
          <main
            className={cn(
              'flex grow flex-col overflow-auto pt-4 pb-11 md:mr-4 md:pl-[calc(16px+var(--sidebar-width))] dark:scheme-only-dark',
              className
            )}
          >
            {children}
          </main>
          <Toaster richColors position="top-right" />
          {user && canEditMaterial && <LedPanelTranscoder />}
        </div>
      </GenericFilterProvider>
    </SidebarProvider>
  );
}
